import { Box } from "@mui/material";
import React from "react";

import {
  FilterValue,
  ProductFilterName,
  ProductFilters,
  ProductFlatFilterName,
  ProductPlaceFilterName,
} from "../../types/productPlane";
import CountedFilterList from "../Filters/CountedFilterList";
import FilterColumn from "../Filters/FilterColumn";
import FilterPanel, { FilterPanelProps } from "../Filters/FilterPanel";
import FilterSection, { FilterSectionProps } from "../Filters/FilterSection";
import FilterTree, { FilterTreeNode } from "../Filters/FilterTree";
import NumericRangeFilter from "../Filters/NumericRangeFilter";
import PlaceFilterList from "./PlaceFilterList";
import {
  EXPLORE_FILTER_LABELS,
  ExploreNarrow,
  NARROWED_FILTERS,
  ONE_CLAIM_NOTE,
  PLACE_FILTERS,
  SPECIFICATIONS_LABEL,
  filterValueLabel,
  specificationLabel,
} from "./exploreFilters";
import {
  EXPLORE_FILTER_NAMES,
  ExploreFilterState,
  countExploreFilters,
  withExplorePick,
  withExploreRange,
} from "./exploreState";

// How the filters are laid out: one under the other in a side panel, each
// folding, or side by side in a row, each a column
export type ExploreFilterLayout = "side" | "row";

// Width (px) a filter takes at the least in a row
export const FILTER_ROW_COLUMN_WIDTH = 224;

export interface ExploreFilterPanelProps {
  // The filters of the answer, each value with the count the backend sent
  filters: ProductFilters;
  // The state of the address: what is picked is read from it
  state: ExploreFilterState;
  onChange: (change: Partial<ExploreFilterState>) => void;
  // The text typed into the box of one long filter; null for none
  narrow: ExploreNarrow | null;
  onNarrowChange: (narrow: ExploreNarrow | null) => void;
  // Called when the rest of a cut list is asked for
  onShowAll: () => void;
  // The side panel unless a row is asked for
  layout?: ExploreFilterLayout;
}

// The box around the filters: the side panel, or a row in which each filter
// is at least a column wide. The filters that do not fit go onto a further
// row: none is hidden behind a scroll bar.
const Frame: React.FC<FilterPanelProps & { layout: ExploreFilterLayout }> = ({
  layout,
  activeCount,
  children,
}) =>
  layout === "side" ? (
    <FilterPanel activeCount={activeCount}>{children}</FilterPanel>
  ) : (
    <Box
      role="group"
      aria-label="Filters"
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${FILTER_ROW_COLUMN_WIDTH}px, 1fr))`,
        // Each filter is a card: the gap between two sets them apart
        gap: 1.5,
        alignItems: "start",
      }}
    >
      {children}
    </Box>
  );

// One filter: a folding section of the side panel, or a column of the row,
// which does not fold and is marked while it holds `active` picks
const Part: React.FC<
  FilterSectionProps & { layout: ExploreFilterLayout; active: number }
> = ({ layout, active, defaultOpen, ...section }) =>
  layout === "side" ? (
    <FilterSection defaultOpen={defaultOpen} {...section} />
  ) : (
    <FilterColumn active={active} {...section} />
  );

const toTreeNode = (value: FilterValue): FilterTreeNode => ({
  value: value.value,
  label: filterValueLabel(value),
  count: value.count,
  children: value.children.map(toTreeNode),
});

const isNarrowed = (name: string): name is ProductFlatFilterName =>
  (NARROWED_FILTERS as readonly string[]).includes(name);

const isPlaceFilter = (name: string): name is ProductPlaceFilterName =>
  (PLACE_FILTERS as readonly string[]).includes(name);

// The sections that start open whatever is picked: the ones a search is
// mostly narrowed by. The rest start folded unless they hold a pick.
const OPEN_FILTERS: readonly ProductFilterName[] = [
  "category",
  "capability",
  "mission",
  "condition",
];

// Whether this build has a section for a filter. A backend newer than the
// page that is open sends filters the page has no state for (FRONTEND-6).
const isKnownFilter = (name: string): name is ProductFilterName =>
  (EXPLORE_FILTER_NAMES as readonly string[]).includes(name);

// The filter panel of the product search: a section per filter in the order
// the backend sends them, trees as trees and lists as lists, then the
// numeric specifications. The filters come whole with the results; the
// panel fetches nothing. Each section's head counts its values (the
// specifications: its keys); category, capability, condition and every
// section with a pick or a range start open. Laid out in a row, the sections
// are columns, all in view at once. A filter this build does not know is
// left out.
const ExploreFilterPanel: React.FC<ExploreFilterPanelProps> = ({
  filters,
  state,
  onChange,
  narrow,
  onNarrowChange,
  onShowAll,
  layout = "side",
}) => (
  <Frame layout={layout} activeCount={countExploreFilters(state)}>
    {filters.choices.filter((filter) => isKnownFilter(filter.name)).map((filter) => {
      const label = EXPLORE_FILTER_LABELS[filter.name];
      const onToggle = (value: string, picked: boolean) =>
        onChange({
          [filter.name]: withExplorePick(state[filter.name], value, picked),
        });

      return (
        <Part
          layout={layout}
          active={state[filter.name].length}
          key={filter.name}
          label={label}
          defaultOpen={
            OPEN_FILTERS.includes(filter.name) || state[filter.name].length > 0
          }
          count={filter.total_values}
          note={filter.name === "condition" ? ONE_CLAIM_NOTE : undefined}
        >
          {filter.shape === "tree" ? (
            <FilterTree
              label={label}
              nodes={filter.values.map(toTreeNode)}
              selected={state[filter.name]}
              onToggle={onToggle}
            />
          ) : isPlaceFilter(filter.name) ? (
            <PlaceFilterList
              label={label}
              values={filter.values}
              selected={state[filter.name]}
              onToggle={onToggle}
              narrowText={narrow?.filter === filter.name ? narrow.text : ""}
              onNarrowChange={(text: string) =>
                onNarrowChange(
                  text
                    ? { filter: filter.name as ProductFlatFilterName, text }
                    : null,
                )
              }
            />
          ) : (
            <CountedFilterList
              label={label}
              values={filter.values.map((value) => ({
                value: value.value,
                label: filterValueLabel(value),
                count: value.count,
              }))}
              total={filter.total_values}
              selected={state[filter.name]}
              onToggle={onToggle}
              onShowAll={onShowAll}
              {...(isNarrowed(filter.name)
                ? {
                    narrowText:
                      narrow?.filter === filter.name ? narrow.text : "",
                    onNarrowChange: (text: string) =>
                      onNarrowChange(
                        text
                          ? {
                              filter: filter.name as ProductFlatFilterName,
                              text,
                            }
                          : null,
                      ),
                  }
                : {})}
            />
          )}
        </Part>
      );
    })}

    {filters.numeric.length > 0 && (
      <Part
        layout={layout}
        active={state.numeric.length}
        label={SPECIFICATIONS_LABEL}
        defaultOpen={state.numeric.length > 0}
        count={filters.numeric.length}
      >
        <NumericRangeFilter
          specs={filters.numeric.map((spec) => ({
            key: spec.key,
            label: specificationLabel(spec.key),
            unit: spec.unit,
            count: spec.count,
            minimum: spec.minimum,
            maximum: spec.maximum,
          }))}
          ranges={state.numeric}
          onChange={(range) =>
            onChange({ numeric: withExploreRange(state.numeric, range) })
          }
        />
      </Part>
    )}
  </Frame>
);

export default ExploreFilterPanel;
